// src/components/AIResponse.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import { matchServicesFromAI as matchServicesFromAI_ES } from "../utils/serviceMatcher_es";
import { matchServicesFromAI as matchServicesFromAI_EN } from "../utils/serviceMatcher_en";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { formatName } from "../utils/formatName";
import { isGibberish } from "../utils/isGibberish";
import { BACKEND_BASE_URL } from "../config/appUrls";

// AI response component
// This component fetches a response from AI based on the user's input
// and displays it in the chat.
// It simulates a business advisory scenario for small Latino businesses.

const AIResponse = props => {
  const { t } = useTranslation();
  const [response, setResponse] = useState(t("pacobot.loading"));
  const [hasSent, setHasSent] = useState(false); // 🛡️ prevent duplicate sending

  const locale = i18n.language;
  const matchServicesFromAI =
    locale === "es" ? matchServicesFromAI_ES : matchServicesFromAI_EN;

  useEffect(() => {
    let isMounted = true;

    const steps = props.steps;

    if (
      !steps ||
      /*  !steps.name?.value || */
      /*   !(steps.manualTypeEntry?.value || steps.gptTypeChecker?.value) || */
      /*   !steps.goalInput?.value || */
      hasSent // 🛑 Already sent
    ) {
      return;
    }

    setHasSent(true); // ✅ Flag to stop multiple sends

    const rawName = steps.name?.value || "";
    const name = formatName(rawName, {
      locale: locale || "en",
      offensiveWords: t("offensiveNames", { returnObjects: true }),
      fallbackName: t("pacobot.fallbackName")
    });

    const businessType = (
      steps.gptTypeChecker?.value || t("pacobot.fallbackBusiness")
    )
      .trim()
      .toLowerCase();

    let goal = steps.goalInput?.value?.trim();

    if (!goal || isGibberish(goal, locale)) {
      console.warn("⚠️ Rejected invalid/gibberish goal description:", goal);
      goal = t("pacobot.defaultGoal").toLowerCase();
    }
    const isFollowUp = steps.followUpQuestion?.value != null;

    const userMessage = isFollowUp
      ? t("aiMessages.followUpMessage", {
          name,
          businessType,
          goal,
          followUpQuestion: steps.followUpQuestion.value
        })
      : t("aiMessages.initialMessage", { name, businessType, goal });

    console.log("📨 Sending to backend:", { message: userMessage });

    const fetchResponse = async () => {
      try {
        const res = await axios.post(`${BACKEND_BASE_URL}/api/genAI`, {
          message: userMessage,
          businessType,
          locale,
          goal
        });

        if (!isMounted) return;

        if (res.data?.reply) {
          setResponse(res.data.reply);
          const suggestedServices = matchServicesFromAI(res.data.reply);
          console.log("🧠 Matched services:", suggestedServices);

          // Save matched services in hidden step
          if (props.triggerNextStep) {
            props.triggerNextStep({
              value: suggestedServices,
              trigger: "solutionOptions"
            });
          }

          setTimeout(() => {
            if (props.triggerNextStep) props.triggerNextStep();
          }, 30000);
        } else {
          setResponse("⚠️ Not a valid response from AI.");
          setTimeout(() => {
            if (props.triggerNextStep) props.triggerNextStep();
          }, 1000);
        }
      } catch (err) {
        console.error("❌ Failed to get a response from AI agent:", err);
        if (isMounted) {
          setResponse("❌ Failed to get a response from Mercadito AI.");
        }
      }
    };

    fetchResponse();

    return () => {
      isMounted = false;
    };
  }, [props.steps]);

  return <div>{response}</div>;
};

export default AIResponse;
